import { openDB } from "idb";
import sodium from "libsodium-wrappers-sumo";
import type { DeviceIdentity, DeviceRegistrationPayload } from "./types";

type PassphraseVault = Readonly<{
  ciphertext: string;
  nonce: string;
  salt: string;
  version: 1;
}>;

type SystemVault = Readonly<{
  ciphertext: string;
  credentialId: string;
  nonce: string;
  prfSalt: string;
  version: 2;
}>;

type AutomaticVault = Readonly<{
  ciphertext: string;
  iv: string;
  version: 3;
}>;

type NativeSystemVault = Readonly<{
  ciphertext: string;
  iv: string;
  version: 4;
}>;

type EncryptedVault = PassphraseVault | SystemVault | AutomaticVault | NativeSystemVault;

export type VaultProtection = "none" | "automatic" | "passphrase" | "system";
export type AppLockPreference = "passphrase" | "system" | null;

export type SystemVaultSetup = Readonly<{
  credentialId: string;
  key: Uint8Array;
  prfSalt: string;
}>;

type PrfExtensionResults = Readonly<{
  prf?: Readonly<{
    enabled?: boolean;
    results?: Readonly<{ first?: BufferSource }>;
  }>;
}>;

let databasePromise: ReturnType<typeof openDB> | null = null;
let nativeRequestSequence = 0;

function nativeVaultAvailable(): boolean {
  return typeof (window as unknown as { ReactNativeWebView?: { postMessage?: unknown } }).ReactNativeWebView?.postMessage === "function";
}

function nativeVaultRequest(type: "vault:get" | "vault:put" | "vault:remove", key: string, value?: string, authenticationRequired = true): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const bridge = (window as unknown as { ReactNativeWebView: { postMessage: (payload: string) => void } }).ReactNativeWebView;
    const requestId = `vault-${Date.now()}-${nativeRequestSequence += 1}`;
    const timeout = window.setTimeout(() => {
      window.removeEventListener("jtuc:native-response", receive as EventListener);
      reject(new Error("Мобильное защищённое хранилище не ответило"));
    }, 60_000);
    const receive = (event: CustomEvent<{ requestId?: string; value?: string | null; error?: string }>) => {
      if (event.detail?.requestId !== requestId) return;
      window.clearTimeout(timeout);
      window.removeEventListener("jtuc:native-response", receive as EventListener);
      if (event.detail.error) reject(new Error(event.detail.error));
      else resolve(event.detail.value ?? null);
    };
    window.addEventListener("jtuc:native-response", receive as EventListener);
    bridge.postMessage(JSON.stringify({ type, requestId, key, value, authenticationRequired }));
  });
}

function vaultDatabase(): ReturnType<typeof openDB> {
  databasePromise ??= openDB("jtuc-live", 2, {
    upgrade(database) {
      if (!database.objectStoreNames.contains("vaults")) database.createObjectStore("vaults");
      if (!database.objectStoreNames.contains("automaticVaultKeys")) database.createObjectStore("automaticVaultKeys");
    },
  });
  return databasePromise;
}

function keyName(username: string): string {
  return username.trim().toLowerCase();
}

function appLockKey(username: string): string {
  return `jtuc:app-lock:${keyName(username)}`;
}

function vaultModeKey(username: string): string {
  return `jtuc:vault-mode:${keyName(username)}`;
}

function automaticKeyName(username: string): string {
  return `automatic-key:${keyName(username)}`;
}

function nativeSystemKeyName(username: string): string {
  return `system-lock-key:${keyName(username)}`;
}

export function appLockPreference(username: string): AppLockPreference {
  const value = localStorage.getItem(appLockKey(username));
  return value === "passphrase" || value === "system" ? value : null;
}

export function setAppLockPreference(username: string, protection: "automatic" | "passphrase" | "system"): void {
  if (protection === "automatic") localStorage.removeItem(appLockKey(username));
  else localStorage.setItem(appLockKey(username), protection);
}

function b64(value: Uint8Array | ArrayBuffer): string {
  const data = value instanceof Uint8Array ? value : new Uint8Array(value);
  return sodium.to_base64(data, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function bytes(value: string): Uint8Array {
  return sodium.from_base64(value, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function cryptoBytes(value: Uint8Array): Uint8Array<ArrayBuffer> {
  const copied = new Uint8Array(new ArrayBuffer(value.byteLength));
  copied.set(value);
  return copied;
}

function validateIdentity(value: Uint8Array): DeviceIdentity {
  const parsed = JSON.parse(sodium.to_string(value)) as DeviceIdentity;
  if (!parsed.id || !parsed.encryptionPrivateKey || !parsed.signingPrivateKey) throw new Error("Некорректный vault");
  return parsed;
}

async function readVault(username: string): Promise<EncryptedVault | undefined> {
  const mode = localStorage.getItem(vaultModeKey(username));
  if (mode === "automatic" || mode === "native-system") {
    const database = await vaultDatabase();
    return database.get("vaults", keyName(username)) as Promise<EncryptedVault | undefined>;
  }
  if (nativeVaultAvailable()) {
    const stored = await nativeVaultRequest("vault:get", keyName(username), undefined, mode !== "passphrase");
    if (stored) return JSON.parse(stored) as EncryptedVault;
  }
  const database = await vaultDatabase();
  return database.get("vaults", keyName(username)) as Promise<EncryptedVault | undefined>;
}

async function writeVault(username: string, vault: EncryptedVault): Promise<void> {
  if (vault.version === 3 || vault.version === 4) {
    const database = await vaultDatabase();
    await database.put("vaults", vault, keyName(username));
    localStorage.setItem(vaultModeKey(username), vault.version === 3 ? "automatic" : "native-system");
    return;
  }
  if (nativeVaultAvailable()) {
    await nativeVaultRequest("vault:put", keyName(username), JSON.stringify(vault), vault.version !== 1);
  } else {
    const database = await vaultDatabase();
    await database.put("vaults", vault, keyName(username));
  }
  localStorage.setItem(vaultModeKey(username), vault.version === 1 ? "passphrase" : "protected");
}

async function automaticVaultKey(username: string, create: boolean): Promise<CryptoKey> {
  await sodium.ready;
  if (nativeVaultAvailable()) {
    let stored = await nativeVaultRequest("vault:get", automaticKeyName(username), undefined, false);
    if (!stored && create) {
      const raw = crypto.getRandomValues(new Uint8Array(32));
      try {
        stored = b64(raw);
        await nativeVaultRequest("vault:put", automaticKeyName(username), stored, false);
      } finally {
        raw.fill(0);
      }
    }
    if (!stored) throw new Error("Ключ автоматического входа не найден в защищённом хранилище устройства");
    const raw = bytes(stored);
    try {
      return await crypto.subtle.importKey("raw", cryptoBytes(raw), "AES-GCM", false, ["encrypt", "decrypt"]);
    } finally {
      raw.fill(0);
    }
  }
  const database = await vaultDatabase();
  const existing = await database.get("automaticVaultKeys", keyName(username)) as CryptoKey | undefined;
  if (existing) return existing;
  if (!create) throw new Error("Ключ автоматического входа не найден в профиле браузера");
  const generated = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  await database.put("automaticVaultKeys", generated, keyName(username));
  return generated;
}

async function nativeSystemVaultKey(username: string, create: boolean): Promise<CryptoKey> {
  await sodium.ready;
  if (!nativeVaultAvailable()) throw new Error("Системная блокировка приложения доступна только в мобильном приложении");
  let stored: string | null;
  if (create) {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    try {
      stored = b64(raw);
      await nativeVaultRequest("vault:put", nativeSystemKeyName(username), stored, true);
    } finally {
      raw.fill(0);
    }
  } else {
    stored = await nativeVaultRequest("vault:get", nativeSystemKeyName(username), undefined, true);
  }
  if (!stored) throw new Error("Системный ключ приложения не найден");
  const raw = bytes(stored);
  try {
    return await crypto.subtle.importKey("raw", cryptoBytes(raw), "AES-GCM", false, ["encrypt", "decrypt"]);
  } finally {
    raw.fill(0);
  }
}

async function removeAutomaticVaultKey(username: string): Promise<void> {
  if (nativeVaultAvailable()) {
    await nativeVaultRequest("vault:remove", automaticKeyName(username), undefined, false);
  } else {
    const database = await vaultDatabase();
    await database.delete("automaticVaultKeys", keyName(username));
  }
}

async function removeAutomaticVaultArtifacts(username: string): Promise<void> {
  await removeAutomaticVaultKey(username);
  const database = await vaultDatabase();
  const stored = await database.get("vaults", keyName(username)) as EncryptedVault | undefined;
  if (stored?.version === 3) await database.delete("vaults", keyName(username));
}

async function removeNativeSystemArtifacts(username: string): Promise<void> {
  if (nativeVaultAvailable()) await nativeVaultRequest("vault:remove", nativeSystemKeyName(username), undefined, false);
  const database = await vaultDatabase();
  const stored = await database.get("vaults", keyName(username)) as EncryptedVault | undefined;
  if (stored?.version === 4) await database.delete("vaults", keyName(username));
}

async function removeLegacyNativeVault(username: string): Promise<void> {
  if (nativeVaultAvailable()) await nativeVaultRequest("vault:remove", keyName(username), undefined, false);
}

async function deriveVaultKey(password: string, salt: Uint8Array): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_pwhash(
    sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES,
    password,
    salt,
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

function webAuthnError(error: unknown): Error {
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return new Error("Системная проверка отменена или не была завершена вовремя");
  }
  if (error instanceof DOMException && error.name === "SecurityError") {
    return new Error("Системная защита недоступна для этого адреса сайта");
  }
  if (error instanceof DOMException && error.name === "NotSupportedError") {
    return new Error("Этот браузер или системный аутентификатор не поддерживает защищённое хранилище WebAuthn PRF");
  }
  return error instanceof Error ? error : new Error("Не удалось выполнить системную проверку устройства");
}

function prfKey(credential: PublicKeyCredential): Uint8Array | null {
  const outputs = credential.getClientExtensionResults() as AuthenticationExtensionsClientOutputs & PrfExtensionResults;
  const first = outputs.prf?.results?.first;
  if (!first || first.byteLength !== sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES) return null;
  const result = first instanceof ArrayBuffer
    ? new Uint8Array(first)
    : new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
  return new Uint8Array(result);
}

async function requestPrfKey(credentialId: Uint8Array, prfSalt: Uint8Array): Promise<Uint8Array> {
  const publicKey = {
    challenge: crypto.getRandomValues(new Uint8Array(32)),
    allowCredentials: [{ id: credentialId, type: "public-key", transports: ["internal"] }],
    userVerification: "required",
    timeout: 60_000,
    extensions: { prf: { eval: { first: prfSalt } } },
  } as unknown as PublicKeyCredentialRequestOptions;
  let assertion: Credential | null;
  try {
    assertion = await navigator.credentials.get({ publicKey });
  } catch (error) {
    throw webAuthnError(error);
  }
  if (!(assertion instanceof PublicKeyCredential)) throw new Error("Системная проверка не вернула ключ устройства");
  const key = prfKey(assertion);
  if (!key) throw new Error("Этот браузер или системный аутентификатор не поддерживает защищённое хранилище WebAuthn PRF");
  return key;
}

export async function systemVaultAvailable(): Promise<boolean> {
  if (!window.isSecureContext || !("PublicKeyCredential" in window) || !navigator.credentials) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export async function createSystemVaultSetup(): Promise<SystemVaultSetup> {
  await sodium.ready;
  if (!(await systemVaultAvailable())) {
    throw new Error("Windows Hello, Touch ID или системная биометрия недоступны. Используйте локальную защитную фразу");
  }
  const prfSalt = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = {
    challenge: crypto.getRandomValues(new Uint8Array(32)),
    rp: { name: "JTUC Live" },
    user: {
      id: crypto.getRandomValues(new Uint8Array(32)),
      name: "jtuc-live-local-vault",
      displayName: "Ключи JTUC Live",
    },
    pubKeyCredParams: [
      { alg: -7, type: "public-key" },
      { alg: -257, type: "public-key" },
    ],
    authenticatorSelection: {
      authenticatorAttachment: "platform",
      residentKey: "discouraged",
      userVerification: "required",
    },
    attestation: "none",
    timeout: 60_000,
    extensions: { prf: { eval: { first: prfSalt } } },
  } as unknown as PublicKeyCredentialCreationOptions;
  let created: Credential | null;
  try {
    created = await navigator.credentials.create({ publicKey });
  } catch (error) {
    throw webAuthnError(error);
  }
  if (!(created instanceof PublicKeyCredential)) throw new Error("Не удалось создать системную защиту устройства");
  const credentialId = new Uint8Array(created.rawId);
  const key = prfKey(created) ?? await requestPrfKey(credentialId, prfSalt);
  return { credentialId: b64(credentialId), prfSalt: b64(prfSalt), key };
}

export async function vaultProtection(username: string): Promise<VaultProtection> {
  const vault = await readVault(username);
  if (!vault) return "none";
  if (vault.version === 3) return "automatic";
  return vault.version === 2 || vault.version === 4 ? "system" : "passphrase";
}

export async function hasVault(username: string): Promise<boolean> {
  return (await readVault(username)) !== undefined;
}

export async function loadIdentity(username: string, password: string): Promise<DeviceIdentity> {
  await sodium.ready;
  const vault = await readVault(username);
  if (!vault) throw new Error("На этом устройстве нет сохранённых ключей пользователя");
  if (vault.version !== 1) throw new Error("Ключи этого устройства защищены системной проверкой");
  const key = await deriveVaultKey(password, bytes(vault.salt));
  try {
    const plain = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      bytes(vault.ciphertext),
      sodium.from_string(`JTUC-VAULT-V1:${keyName(username)}`),
      bytes(vault.nonce),
      key,
    );
    return validateIdentity(plain);
  } catch {
    throw new Error("Не удалось открыть ключи: неверный пароль или vault повреждён");
  } finally {
    key.fill(0);
  }
}

export async function loadIdentityWithSystem(username: string): Promise<DeviceIdentity> {
  await sodium.ready;
  const vault = await readVault(username);
  if (!vault) throw new Error("На этом устройстве нет сохранённых ключей пользователя");
  if (vault.version === 4) {
    const key = await nativeSystemVaultKey(username, false);
    try {
      const plain = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: cryptoBytes(bytes(vault.iv)),
          additionalData: new TextEncoder().encode(`JTUC-VAULT-V4:${keyName(username)}`),
        },
        key,
        cryptoBytes(bytes(vault.ciphertext)),
      );
      return validateIdentity(new Uint8Array(plain));
    } catch {
      throw new Error("Системная проверка пройдена, но локальное хранилище ключей повреждено");
    }
  }
  if (vault.version !== 2) throw new Error("Ключи этого устройства защищены локальной фразой");
  const key = await requestPrfKey(bytes(vault.credentialId), bytes(vault.prfSalt));
  try {
    const plain = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      cryptoBytes(bytes(vault.ciphertext)),
      sodium.from_string(`JTUC-VAULT-V2:${keyName(username)}`),
      bytes(vault.nonce),
      key,
    );
    return validateIdentity(plain);
  } catch {
    throw new Error("Системная проверка пройдена, но локальное хранилище ключей повреждено");
  } finally {
    key.fill(0);
  }
}

export async function loadIdentityAutomatic(username: string): Promise<DeviceIdentity> {
  const vault = await readVault(username);
  if (!vault) throw new Error("На этом устройстве нет сохранённых ключей пользователя");
  if (vault.version !== 3) throw new Error("Для локальных ключей включена дополнительная защита");
  const key = await automaticVaultKey(username, false);
  try {
    const plain = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: cryptoBytes(bytes(vault.iv)),
        additionalData: new TextEncoder().encode(`JTUC-VAULT-V3:${keyName(username)}`),
      },
      key,
      cryptoBytes(bytes(vault.ciphertext)),
    );
    return validateIdentity(new Uint8Array(plain));
  } catch {
    throw new Error("Локальное защищённое хранилище повреждено");
  }
}

export async function saveIdentity(username: string, password: string, identity: DeviceIdentity): Promise<void> {
  await sodium.ready;
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const key = await deriveVaultKey(password, salt);
  try {
    const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      sodium.from_string(JSON.stringify(identity)),
      sodium.from_string(`JTUC-VAULT-V1:${keyName(username)}`),
      null,
      nonce,
      key,
    );
    const vault: PassphraseVault = { version: 1, salt: b64(salt), nonce: b64(nonce), ciphertext: b64(ciphertext) };
    await writeVault(username, vault);
    await removeAutomaticVaultArtifacts(username);
    await removeNativeSystemArtifacts(username);
  } finally {
    key.fill(0);
  }
}

export async function saveIdentityWithSystem(username: string, setup: SystemVaultSetup, identity: DeviceIdentity): Promise<void> {
  await sodium.ready;
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  try {
    const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      sodium.from_string(JSON.stringify(identity)),
      sodium.from_string(`JTUC-VAULT-V2:${keyName(username)}`),
      null,
      nonce,
      setup.key,
    );
    const vault: SystemVault = {
      version: 2,
      credentialId: setup.credentialId,
      prfSalt: setup.prfSalt,
      nonce: b64(nonce),
      ciphertext: b64(ciphertext),
    };
    await writeVault(username, vault);
    await removeAutomaticVaultArtifacts(username);
    await removeNativeSystemArtifacts(username);
  } finally {
    setup.key.fill(0);
  }
}

export async function saveIdentityWithNativeSystem(username: string, identity: DeviceIdentity): Promise<void> {
  const key = await nativeSystemVaultKey(username, true);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(`JTUC-VAULT-V4:${keyName(username)}`),
    },
    key,
    new TextEncoder().encode(JSON.stringify(identity)),
  );
  await writeVault(username, { version: 4, iv: b64(iv), ciphertext: b64(ciphertext) });
  await removeAutomaticVaultKey(username);
  await removeLegacyNativeVault(username);
  setAppLockPreference(username, "system");
}

export async function saveIdentityAutomatic(username: string, identity: DeviceIdentity): Promise<void> {
  const key = await automaticVaultKey(username, true);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(`JTUC-VAULT-V3:${keyName(username)}`),
    },
    key,
    new TextEncoder().encode(JSON.stringify(identity)),
  );
  await writeVault(username, { version: 3, iv: b64(iv), ciphertext: b64(ciphertext) });
  await removeNativeSystemArtifacts(username);
  await removeLegacyNativeVault(username);
  setAppLockPreference(username, "automatic");
}

export async function createIdentity(): Promise<DeviceIdentity> {
  await sodium.ready;
  const encryption = sodium.crypto_box_keypair();
  const signing = sodium.crypto_sign_keypair();
  return {
    id: crypto.randomUUID(),
    encryptionPublicKey: b64(encryption.publicKey),
    encryptionPrivateKey: b64(encryption.privateKey),
    signingPublicKey: b64(signing.publicKey),
    signingPrivateKey: b64(signing.privateKey),
  };
}

export async function prepareIdentity(username: string, password: string): Promise<{
  identity: DeviceIdentity;
  existing: boolean;
  protection: VaultProtection;
}> {
  const protection = await vaultProtection(username);
  if (protection === "automatic") return { identity: await loadIdentityAutomatic(username), existing: true, protection };
  if (protection === "system") return { identity: await loadIdentityWithSystem(username), existing: true, protection };
  if (protection === "passphrase") return { identity: await loadIdentity(username, password), existing: true, protection };
  return { identity: await createIdentity(), existing: false, protection };
}

export function devicePayload(identity: DeviceIdentity): DeviceRegistrationPayload {
  const platform = /Android/i.test(navigator.userAgent) ? "android" : /iPhone|iPad/i.test(navigator.userAgent) ? "ios" : "web";
  return {
    id: identity.id,
    name: `${platform === "web" ? "Браузер" : "Мобильное устройство"} · ${navigator.platform || "JTUC"}`.slice(0, 100),
    platform,
    encryptionPublicKey: identity.encryptionPublicKey,
    signingPublicKey: identity.signingPublicKey,
  };
}
